import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Car, Home as HomeIcon, Layers, Shield, HeartPulse, Activity, Briefcase, Zap, Bike, Plane, PawPrint, LogOut, RefreshCw, Filter, Mail, Phone, ChevronRight, Download, FileText, Paperclip } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import Logo from "../components/Logo";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";

const TYPE_META = {
  auto:          { label: "Auto",           icon: Car },
  casa:          { label: "Casa",           icon: HomeIcon },
  auto_casa:     { label: "Auto + Casa",    icon: Layers },
  vita:          { label: "Vita",           icon: HeartPulse },
  infortuni:     { label: "Infortuni",      icon: Activity },
  rc_pro:        { label: "RC Pro",         icon: Briefcase },
  monopattino:   { label: "Monopattino",    icon: Zap },
  moto:          { label: "Moto",           icon: Bike },
  viaggi:        { label: "Viaggi",         icon: Plane },
  tutela_legale: { label: "Tutela legale",  icon: Shield },
  pet:           { label: "Cani e Gatti",   icon: PawPrint },
  altro:         { label: "Altro",          icon: Shield },
};

const STATUS_META = {
  new:       { label: "Nuova",     cls: "bg-[#C5A059]/15 text-[#8a6f35] border-[#C5A059]/40" },
  contacted: { label: "Contattata",cls: "bg-slate-900 text-white border-slate-900" },
  quoted:    { label: "Preventivata", cls: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  archived:  { label: "Archiviata",cls: "bg-slate-100 text-slate-600 border-slate-200" },
};

function StatusPill({ status }) {
  const m = STATUS_META[status] || STATUS_META.new;
  return <span className={`inline-flex items-center px-2.5 py-0.5 text-[11px] border ${m.cls}`}>{m.label}</span>;
}

function fmtDate(d) {
  try {
    return new Date(d).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return String(d); }
}

function fmtSize(n) {
  if (!n && n !== 0) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

async function downloadAttachment(quoteId, index, att) {
  try {
    const res = await api.get(`/admin/quotes/${quoteId}/attachments/${index}`, {
      responseType: "blob",
    });
    const blob = new Blob([res.data], { type: att.content_type || "application/octet-stream" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = att.filename || `allegato-${index + 1}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  } catch (e) {
    toast.error(formatApiError(e?.response?.data?.detail) || e.message);
  }
}

export default function Admin() {
  const { user, logout } = useAuth();
  const [quotes, setQuotes] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0, contacted: 0, quoted: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter) params.status = filter;
      if (typeFilter) params.quote_type = typeFilter;
      const [q, s] = await Promise.all([
        api.get("/admin/quotes", { params }),
        api.get("/admin/stats"),
      ]);
      setQuotes(q.data);
      setStats(s.data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter, typeFilter]);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/quotes/${id}`, { status });
      toast.success("Stato aggiornato");
      if (selected && selected.id === id) setSelected({ ...selected, status });
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="admin-brand">
            <Logo className="w-8 h-8 text-slate-900" />
            <div className="font-serif text-slate-900">Da Pra · Admin</div>
          </Link>
          <div className="flex items-center gap-4">
            <span className="text-xs text-slate-500 hidden sm:inline">{user?.email}</span>
            <button onClick={logout} data-testid="admin-logout" className="inline-flex items-center gap-2 text-sm text-slate-700 hover:text-slate-900">
              <LogOut size={14} /> Esci
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-10 py-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <div className="overline">Dashboard</div>
            <h1 className="mt-2 font-serif text-3xl md:text-4xl text-slate-900">Richieste di preventivo</h1>
          </div>
          <button onClick={load} data-testid="admin-refresh" className="inline-flex items-center gap-2 text-sm text-slate-700 hover:text-slate-900">
            <RefreshCw size={14} /> Aggiorna
          </button>
        </div>

        {/* Stats */}
        <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { k: "total", label: "Totale", value: stats.total },
            { k: "new", label: "Nuove", value: stats.new },
            { k: "contacted", label: "Contattate", value: stats.contacted },
            { k: "quoted", label: "Preventivate", value: stats.quoted },
          ].map((c) => (
            <div key={c.k} data-testid={`stat-${c.k}`} className="bg-white border border-slate-200 p-5">
              <div className="overline">{c.label}</div>
              <div className="mt-3 font-serif text-3xl text-slate-900">{c.value}</div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="mt-10 flex flex-wrap items-center gap-2 text-xs">
          <Filter size={14} className="text-slate-500" />
          <span className="text-slate-500 uppercase tracking-wide">Filtra stato:</span>
          {[
            { k: "", label: "Tutte" },
            { k: "new", label: "Nuove" },
            { k: "contacted", label: "Contattate" },
            { k: "quoted", label: "Preventivate" },
            { k: "archived", label: "Archiviate" },
          ].map((f) => (
            <button
              key={f.k || "all"}
              onClick={() => setFilter(f.k)}
              data-testid={`filter-${f.k || "all"}`}
              className={`px-3 h-8 border text-xs ${filter === f.k ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-300 text-slate-700 hover:border-slate-900"}`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Type filter */}
        <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
          <Filter size={14} className="text-slate-500" />
          <span className="text-slate-500 uppercase tracking-wide">Filtra prodotto:</span>
          <button
            onClick={() => setTypeFilter("")}
            data-testid="type-filter-all"
            className={`px-3 h-8 border text-xs ${typeFilter === "" ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-300 text-slate-700 hover:border-slate-900"}`}
          >
            Tutti
          </button>
          {Object.entries(TYPE_META).filter(([k]) => k !== "altro").map(([k, meta]) => {
            const Icon = meta.icon;
            const active = typeFilter === k;
            return (
              <button
                key={k}
                onClick={() => setTypeFilter(k)}
                data-testid={`type-filter-${k}`}
                className={`inline-flex items-center gap-1.5 px-3 h-8 border text-xs ${active ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-300 text-slate-700 hover:border-slate-900"}`}
              >
                <Icon size={12} /> {meta.label}
              </button>
            );
          })}
        </div>

        {/* Table */}
        <div className="mt-6 bg-white border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="quotes-table">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                  <th className="p-4">Data</th>
                  <th className="p-4">Cliente</th>
                  <th className="p-4">Tipologia</th>
                  <th className="p-4">Contatto</th>
                  <th className="p-4">Stato</th>
                  <th className="p-4"></th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={6} className="p-10 text-center text-slate-500">Caricamento…</td></tr>
                )}
                {!loading && quotes.length === 0 && (
                  <tr><td colSpan={6} className="p-10 text-center text-slate-500" data-testid="empty-state">Nessuna richiesta trovata.</td></tr>
                )}
                {!loading && quotes.map((q) => {
                  const Meta = TYPE_META[q.quote_type] || TYPE_META.altro;
                  const Icon = Meta.icon;
                  return (
                    <tr key={q.id} data-testid={`quote-row-${q.id}`} className="border-b border-slate-100 hover:bg-slate-50/60 transition-colors">
                      <td className="p-4 text-slate-500 whitespace-nowrap">{fmtDate(q.created_at)}</td>
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-slate-900">{q.nome} {q.cognome}</span>
                          {q.attachments && q.attachments.length > 0 && (
                            <span
                              data-testid={`attach-badge-${q.id}`}
                              title={`${q.attachments.length} allegato/i`}
                              className="inline-flex items-center gap-1 text-[11px] text-[#8a6f35] bg-[#C5A059]/15 border border-[#C5A059]/40 px-1.5 h-5"
                            >
                              <Paperclip size={10} /> {q.attachments.length}
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-slate-500">#{q.id.slice(0,8).toUpperCase()}</div>
                      </td>
                      <td className="p-4">
                        <span className="inline-flex items-center gap-2 text-slate-800"><Icon size={14} className="text-[#C5A059]" /> {Meta.label}</span>
                      </td>
                      <td className="p-4">
                        <div className="text-slate-800">{q.email}</div>
                        <div className="text-xs text-slate-500">{q.telefono}</div>
                      </td>
                      <td className="p-4"><StatusPill status={q.status} /></td>
                      <td className="p-4 text-right">
                        <button onClick={() => setSelected(q)} data-testid={`view-${q.id}`} className="inline-flex items-center gap-1 text-slate-900 hover:text-[#C5A059] text-sm">
                          Dettagli <ChevronRight size={14} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </main>

      {/* Detail Dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-2xl bg-white">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="font-serif text-2xl text-slate-900">
                  Richiesta #{selected.id.slice(0,8).toUpperCase()}
                </DialogTitle>
                <DialogDescription>
                  Ricevuta il {fmtDate(selected.created_at)}
                </DialogDescription>
              </DialogHeader>

              <div className="flex items-center justify-between mt-2">
                <StatusPill status={selected.status} />
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button data-testid="status-menu" className="text-sm border border-slate-300 hover:border-slate-900 px-3 h-9">Cambia stato</button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="bg-white">
                    {Object.keys(STATUS_META).map((s) => (
                      <DropdownMenuItem key={s} onClick={() => updateStatus(selected.id, s)} data-testid={`status-opt-${s}`}>
                        {STATUS_META[s].label}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              <div className="mt-6 grid sm:grid-cols-2 gap-4">
                <div className="border border-slate-200 p-4">
                  <div className="overline">Cliente</div>
                  <div className="mt-2 font-medium text-slate-900">{selected.nome} {selected.cognome}</div>
                  <div className="mt-2 text-sm text-slate-600 flex items-center gap-2"><Mail size={14} /> <a href={`mailto:${selected.email}`} className="hover:text-slate-900">{selected.email}</a></div>
                  <div className="mt-1 text-sm text-slate-600 flex items-center gap-2"><Phone size={14} /> {selected.telefono}</div>
                </div>
                <div className="border border-slate-200 p-4">
                  <div className="overline">Tipologia</div>
                  <div className="mt-2 font-medium text-slate-900">{(TYPE_META[selected.quote_type] || TYPE_META.altro).label}</div>
                  {selected.note && <p className="mt-3 text-sm text-slate-600 whitespace-pre-wrap">{selected.note}</p>}
                </div>
              </div>

              {selected.auto && (selected.quote_type === "auto" || selected.quote_type === "auto_casa") && (
                <div className="mt-4 border border-slate-200 p-4">
                  <div className="overline">Dati auto</div>
                  <DetailGrid obj={selected.auto} labels={{ targa: "Targa", marca: "Marca", modello: "Modello", anno: "Anno", alimentazione: "Alim.", km_annui: "Km/anno", conducente_nome: "Conducente", conducente_data_nascita: "Data nascita", note: "Note" }} />
                </div>
              )}

              {selected.casa && (selected.quote_type === "casa" || selected.quote_type === "auto_casa") && (
                <div className="mt-4 border border-slate-200 p-4">
                  <div className="overline">Dati casa</div>
                  <DetailGrid obj={selected.casa} labels={{ indirizzo: "Indirizzo", citta: "Città", cap: "CAP", tipo: "Tipologia", mq: "Mq", anno_costruzione: "Anno", antifurto: "Antifurto", coperture: "Coperture", note: "Note" }} />
                </div>
              )}

              {selected.altro && selected.quote_type === "altro" && (
                <div className="mt-4 border border-slate-200 p-4">
                  <div className="overline">Altra copertura</div>
                  <DetailGrid obj={selected.altro} labels={{ tipologia: "Tipologia", descrizione: "Descrizione" }} />
                </div>
              )}

              {selected.vita && selected.quote_type === "vita" && (
                <div className="mt-4 border border-slate-200 p-4">
                  <div className="overline">Ramo vita</div>
                  <DetailGrid obj={selected.vita} labels={{
                    prodotti: "Prodotti",
                    eta: "Età",
                    profilo: "Profilo",
                    obiettivo: "Obiettivo",
                    capitale_stimato: "Capitale/Premio",
                    note: "Note",
                  }} />
                </div>
              )}

              {selected.attachments && selected.attachments.length > 0 && (
                <div className="mt-4 border border-slate-200 p-4" data-testid="admin-attachments">
                  <div className="overline">Allegati ({selected.attachments.length})</div>
                  <ul className="mt-3 divide-y divide-slate-200">
                    {selected.attachments.map((a, i) => (
                      <li key={i} className="flex items-center justify-between py-2 text-sm">
                        <div className="flex items-center gap-3 min-w-0">
                          <span className="w-8 h-8 flex items-center justify-center bg-slate-100 text-slate-700 shrink-0">
                            <FileText size={14} />
                          </span>
                          <div className="min-w-0">
                            <div className="truncate text-slate-900" title={a.filename}>{a.filename}</div>
                            <div className="text-xs text-slate-500">{fmtSize(a.size)} · {a.content_type}</div>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => downloadAttachment(selected.id, i, a)}
                          data-testid={`download-att-${i}`}
                          className="inline-flex items-center gap-1.5 text-slate-900 hover:text-[#C5A059] text-sm"
                        >
                          <Download size={14} /> Scarica
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DetailGrid({ obj, labels }) {
  const entries = Object.entries(labels);
  return (
    <div className="mt-3 grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
      {entries.map(([k, l]) => {
        let v = obj?.[k];
        if (v === null || v === undefined || v === "") return null;
        if (Array.isArray(v)) v = v.join(", ");
        if (typeof v === "boolean") v = v ? "Sì" : "No";
        return (
          <div key={k} className="flex gap-2">
            <span className="text-slate-500 min-w-[110px]">{l}:</span>
            <span className="text-slate-900">{String(v)}</span>
          </div>
        );
      })}
    </div>
  );
}
